import { type WithRouter, withRouter } from 'found';
import React, { useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { compose } from 'redux';

import { fetchCollectionContents, fetchCollections } from '../actions/collections.actions';
import type { AsyncActionStatus } from '../actions/common.actionTypes';
import CuratedCollectionOverview from '../components/CuratedCollectionOverview';
import withAsyncData from '../components/utils/withAsyncData';
import type { StoreType } from '../reducers';
import {
	selectCollectionStatus,
	selectCollectionWithContents,
} from '../reducers/collections.selectors';
import { selectUser } from '../reducers/user.selectors';
import { ampliTrackCollectionViewedEvent } from '../services/track.service';
import type { Collection, Pagination } from '../types/AppContent';
import { createPaginationRequest } from '../utils/pagination';

interface StateProps {
	collection?: Collection;
	collectionStatus: AsyncActionStatus;
	collectionPagination: Pagination;
	user: StoreType['user'];
}
interface DispatchProps {
	fetchCollectionContents: typeof fetchCollectionContents;
}
interface OwnProps {
	match: {
		params: {
			collectionId: string;
		};
	};
}

type CuratedCollectionItemListPageProps = DispatchProps & StateProps & OwnProps & WithRouter;

const CuratedCollectionItemListPage: React.FC<CuratedCollectionItemListPageProps> = ({
	match,
	collection,
	collectionStatus,
	...props
}) => {
	const collectionId = match.params.collectionId;
	const user = useSelector(selectUser);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		props.fetchCollectionContents(collectionId, createPaginationRequest(props.collectionPagination));
		if (user && collection) {
			ampliTrackCollectionViewedEvent(collection);
		}
	}, [collectionId]);

	if (!collection) {
		return null;
	}

	return (
		<CuratedCollectionOverview
			collection={collection}
			collectionStatus={collectionStatus}
			collectionId={collectionId}
			collectionPagination={props.collectionPagination}
			loadNext={() => {
				return props.fetchCollectionContents(
					collectionId,
					createPaginationRequest({
						...props.collectionPagination,
						offset: props.collectionPagination.offset + props.collectionPagination.limit,
					})
				);
			}}
		/>
	);
};

const mapState = (state: StoreType, props: OwnProps): StateProps => ({
	collection: selectCollectionWithContents(state, props.match.params.collectionId),
	collectionStatus: selectCollectionStatus(state, props.match.params.collectionId),
	collectionPagination:
		state.collections.byId[props.match.params.collectionId] &&
		state.collections.byId[props.match.params.collectionId].pagination,
	user: state.user,
});

export default compose<React.FC<Record<string, unknown>>>(
	withRouter,
	connect(mapState, { fetchCollections, fetchCollectionContents }),
	withAsyncData((props) => ({
		refetch: () => props.fetchCollections(),
		dataProp: 'collection',
		statusProp: 'collectionStatus',
	}))
)(CuratedCollectionItemListPage);
